import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const HERO_IMG = 'https://images.unsplash.com/photo-1664994181363-0b051d554afa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxuZXclMjB5b3JrJTIwY2l0eXNjYXBlJTIwYnJvbnglMjBxdWVlbnMlMjBicmlkZ2V8ZW58MHx8fHwxNzc2MTEyNTQ3fDA&ixlib=rb-4.1.0&q=85';

const textVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.3 + i * 0.15, duration: 0.7, ease: 'easeOut' },
  }),
};

export default function Hero() {
  const { scrollYProgress } = useScroll();
  const bgY = useTransform(scrollYProgress, [0, 0.3], [0, -80]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Parallax Background */}
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY }}>
        {/* REPLACE: hero background image with actual firm image */}
        <img
          src={HERO_IMG}
          alt="New York City skyline representing Fitzgerald & Associates service areas"
          className="w-full h-full object-cover scale-110"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B132B]/85 via-[#0B132B]/70 to-[#1D4ED8]/40" />
      </motion.div>

      {/* Decorative Elements */}
      <div className="absolute inset-0 z-[1] overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-cyan-electric/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 left-1/4 w-72 h-72 bg-[#1D4ED8]/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '3s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 w-full">
        <div className="max-w-3xl">
          <motion.div
            custom={0}
            variants={textVariants}
            initial="hidden"
            animate="visible"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm mb-8"
          >
            <div className="w-2 h-2 rounded-full bg-cyan-bright animate-glow-pulse" />
            <span className="text-sm text-white/80 font-body tracking-wide">New York&apos;s Premier Law Firm</span>
          </motion.div>

          <motion.h1
            custom={1}
            variants={textVariants}
            initial="hidden"
            animate="visible"
            data-testid="hero-heading"
            className="font-heading font-bold text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight leading-[1.05] mb-6"
          >
            Raising the Bar{' '}
            <br />
            <span className="bg-gradient-to-r from-[#3B82F6] to-[#22D3EE] bg-clip-text text-transparent">
              of Excellence
            </span>
          </motion.h1>

          <motion.p
            custom={2}
            variants={textVariants}
            initial="hidden"
            animate="visible"
            data-testid="hero-subtitle"
            className="text-lg md:text-xl text-white/70 font-body leading-relaxed max-w-xl mb-10"
          >
            Located in New York, serving Westchester, New York, Queens and Bergen County New Jersey areas with
            uncompromising legal representation and dedicated advocacy.
          </motion.p>

          <motion.div
            custom={3}
            variants={textVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-cta-consultation"
              onClick={() => scrollTo('contact')}
              className="px-8 py-4 rounded-md text-white font-semibold text-base transition-all duration-300 hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #1D4ED8, #06B6D4)' }}
            >
              Free Consultation
            </button>
            <button
              data-testid="hero-cta-learn-more"
              onClick={() => scrollTo('about')}
              className="px-8 py-4 rounded-md font-semibold text-base text-white border border-white/30 bg-white/5 backdrop-blur-sm transition-all duration-300 hover:bg-white/10 hover:border-white/50"
            >
              Learn More About Us
            </button>
          </motion.div>

          {/* Stats */}
          <motion.div
            custom={4}
            variants={textVariants}
            initial="hidden"
            animate="visible"
            className="flex gap-10 mt-16 pt-8 border-t border-white/10"
          >
            {[
              { num: '10+', label: 'Years Experience' },
              { num: '1,000+', label: 'Cases Won' },
              { num: '90%', label: 'Success Rate' },
            ].map(stat => (
              <div key={stat.label} data-testid={`hero-stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="text-2xl md:text-3xl font-heading font-bold text-white">{stat.num}</div>
                <div className="text-sm text-white/50 font-body mt-1">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[var(--color-bg)] to-transparent z-10" />
    </section>
  );
}
